import React from 'react'
import { InputAdornment } from '@mui/material'
import { ErrorOutlined, Warning } from '@mui/icons-material'
import store from 'state/store'
import Tooltip from 'components/generic/tooltip'
import { MEASURE_UNIT } from 'utils/constants'
import { setDecimalPrecision, getUnitConversionFactors } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'

export function isPriceOutsideMinMax(materialPrice, priceEntryPrice, minMaxRange) {
  const state = store.getState()
  const decimalPrecisionSetting = state?.settings?.settings?.purchaseSettings?.inboundTickets?.decimalPrecision || 2
  const roundedMaterialPrice = roundTo(materialPrice, decimalPrecisionSetting)
  const roundedPriceEntryPrice = roundTo(priceEntryPrice, decimalPrecisionSetting)
  return Math.abs(roundTo(roundedMaterialPrice - roundedPriceEntryPrice, decimalPrecisionSetting)) > (minMaxRange || 0)
}

export const getPriceErrorFn =
  ({ isAdmin, priceEntryPrice, minMaxRange }) =>
  value =>
    !isAdmin && isPriceOutsideMinMax(value, priceEntryPrice, minMaxRange)

export function priceValidationInfo({
  materialPrice,
  priceEntry,
  isAdmin,
  priceType = 'buyPrice',
  customerPreferredUm,
  isTransfer,
}) {
  const { defaultYardUm } = store.getState()?.settings || {}

  let priceEntryPrice =
    (priceEntry?.[priceType]?.price?.amount || 0) / 10 ** (priceEntry?.[priceType]?.price?.precision || 4)
  let minMaxRange = priceEntry?.[priceType]?.minMaxRange || 0
  const um = priceEntry?.weightUnits || defaultYardUm
  if (customerPreferredUm && customerPreferredUm !== MEASURE_UNIT.EACH) {
    const UNIT_CONVERSION_FACTORS = getUnitConversionFactors()
    const newUm = UNIT_CONVERSION_FACTORS[customerPreferredUm?.toLowerCase()]
    const oldUm = UNIT_CONVERSION_FACTORS[um?.toLowerCase()]
    priceEntryPrice = parseFloat(priceEntryPrice) * (newUm / oldUm)
    minMaxRange = parseFloat(minMaxRange) * (newUm / oldUm)
  }
  const exceedsMinMax = isPriceOutsideMinMax(materialPrice, priceEntryPrice, minMaxRange)
  let color
  let startAdornment
  let helperText
  const isWarning = isAdmin || isTransfer
  if (exceedsMinMax) {
    helperText = `Price entered is outside of Min/Max Range. Original price: $${setDecimalPrecision(
      priceEntryPrice,
    )}/${(customerPreferredUm || um || defaultYardUm).toUpperCase()}`

    color = isWarning ? 'warning' : 'error'
    const tooltipContent = (
      <>
        Price entered is outside of Min/Max Range.
        <br />
        Original price: ${setDecimalPrecision(priceEntryPrice)}
      </>
    )
    startAdornment = (
      <Tooltip content={tooltipContent} placement="left">
        <InputAdornment position="start">
          {isWarning ? <Warning color="warning" /> : <ErrorOutlined color="error" />}
        </InputAdornment>
      </Tooltip>
    )
  }
  return { color, startAdornment, priceEntryPrice, minMaxRange, helperText }
}

export function canEditPrice({
  id,
  dirtyFields,
  disabled,
  priceWritePermission,
  minMaxRange,
  priceEntryPrice,
  materialPrice,
  isAdmin,
}) {
  const isDirty = dirtyFields?.materialGrid?.[id]?.price
  return !(
    disabled ||
    !priceWritePermission ||
    !minMaxRange ||
    (!isAdmin && isPriceOutsideMinMax(materialPrice, priceEntryPrice, minMaxRange) && !isDirty)
  )
}
